/**
 * Why a typed filter cannot be applied, in words.
 *
 * Kept apart from chips.tsx, which draws the chips with Mantine, so the
 * iPhone app can share it: mobile-ionic/src/search/shared/ holds an exact
 * copy, and a test there fails if the two differ.
 */
import type { Vocab } from '../../vocab';
import type { ChipError } from './query';

/** Why a chip cannot be applied, and what to do about it. */
export function chipErrorText(e: ChipError, v: Vocab): string {
  const noun = (n: 'campaign' | 'supplier' | 'buyer' | 'from' | 'owner' | 'band') =>
    n === 'from' ? `${v.one('campaign')} or ${v.one('supplier')}` : n === 'owner' ? 'member' : n === 'band' ? 'score band' : v.one(n);
  switch (e.code) {
    case 'unknown_key': return `There is no "${e.key}:" filter. Type ? to see them all.`;
    case 'empty': return 'Needs a value after the colon.';
    case 'no_match': return `No ${noun(e.noun)} has that name.`;
    case 'many_match': return `More than one ${noun(e.noun)} matches. Pick one from the list.`;
    case 'bad_status': return 'Not a status. Try delivered, sold, rejected, pending, filtered, duplicate, error or suppressed.';
    case 'bad_date': return 'Not a date. Try today, last7, thismonth, 2026-10-01 or 2026-10-01..2026-10-14.';
    case 'bad_number': return 'Not an amount. Try >5, <=20 or 5..20.';
    case 'bad_repeats': return 'Not a count. Try >1, >=3 or 2.';
    case 'bad_score': return 'Not a score. Try >50, <=20, 20..50 or 45.';
    case 'bad_field': return 'Field names use letters, numbers and _ only.';
    case 'no_negation': return 'Dates, amounts and scores cannot be excluded. Use before:, after: or a range instead.';
  }
}

/** Why an OR search cannot be run as typed. */
export function orErrorText(e: 'too_many'): string {
  switch (e) {
    case 'too_many': return 'Too many ways for these ORs to combine. Use fewer ORs, or fewer brackets.';
  }
}
